import type { ReactNode } from 'react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { q } from '../../api/queries';
import { useNames } from '../../api/names';
import { Countdown, ErrorNotice, FreshnessLabel, PencilMark, VerdictGlyph } from '@bounded/ui/state';
import { Command, Empty, Page, PageHeader, Panel, Skeleton, rowClass } from '@bounded/ui/primitives';
import { agentName, headline, isWaiting, toolName } from '../../lib/approvals';
import { useNow } from '@bounded/ui/time';
import { useT } from '../../i18n';
import { PairInstructions } from '../../components/pair-instructions';
import { versionStatus } from '../../../../../packages/protocol/src/cli-release';

export const Route = createFileRoute('/_app/')({ component: Overview });

type Item = { key: string; mark: ReactNode; body: ReactNode; action: ReactNode };
const link = 'text-boundary hover:underline';

function Overview() {
  const t = useT(); const m = t.overview;
  const now = useNow(1_000);
  const names = useNames();
  const overview = useQuery(q.overview());
  const waitingQuery = useQuery(q.waiting());
  const folders = useQuery(q.workspaces('pending'));
  const pendingFolders = folders.data?.items ?? [];
  const o = overview.data;

  if (overview.error) return <Page><ErrorNotice error={overview.error} onRetry={() => void overview.refetch()} /></Page>;
  if (!o) return <Page><Skeleton className="h-10 w-48" /><Skeleton className="h-48" /></Page>;
  if (o.devices.length === 0 && o.projects.length === 0) return <FirstRun />;

  const items: Item[] = [];
  const waiting = (waitingQuery.data?.items ?? []).filter((a) => isWaiting(a, now));
  for (const a of waiting.slice(0, 3)) items.push({
    key: a.request.approval_id, mark: <VerdictGlyph outcome="ASK" />,
    body: <><span className="font-mono text-sm font-medium">{headline(a)}</span> <span className="text-graphite">{m.onDevice(agentName(a, t.agents) ?? toolName(a, t.inbox.genericTool), names.device(a.device_id))}</span></>,
    action: <span className="flex items-center gap-3"><Countdown until={a.request.expires_at} className="text-sm" /><Link className={link} to="/inbox/$deviceId/$approvalId" params={{ deviceId: a.device_id, approvalId: a.request.approval_id }} search={{ show: 'waiting' }}>{m.review}</Link></span>,
  });
  if (o.approvals.waiting > 3) items.push({ key: 'more', mark: <VerdictGlyph outcome="ASK" />, body: <span className="text-graphite">{m.more(o.approvals.waiting - 3)}</span>, action: <Link className={link} to="/inbox" search={{ show: 'waiting' }}>{m.openInbox}</Link> });
  // A folder an agent started working in, waiting for its project: the agent is on hold there.
  for (const w of pendingFolders) items.push({ key: `ws-${w.id}`, mark: <VerdictGlyph outcome="ASK" />,
    body: <><span className="text-graphite">{t.workspace.overview(w.device_name)}</span> <code>{w.path}</code></>,
    action: <Link className={link} to="/workspaces/$workspaceId" params={{ workspaceId: w.id }}>{t.workspace.choose}</Link> });
  // Below the supported minimum: the device keeps working on what it has, but needs updating.
  for (const d of o.devices) if (versionStatus(d.daemon_version) === 'unsupported') items.push({ key: `ver-${d.id}`, mark: <PencilMark />,
    body: <><span className="font-medium">{d.name}</span> <span className="text-graphite">{t.release.overview(d.daemon_version ?? '')}</span></>,
    action: <Link className={link} to="/devices/$deviceId" params={{ deviceId: d.id }}>{t.release.update}</Link> });
  for (const d of o.devices) if (d.connection !== 'recent') items.push({ key: `rt-${d.id}`, mark: <PencilMark />,
    body: <><span className="font-medium">{d.name}</span> <span className="text-graphite">{d.connection === 'never_reported' ? m.neverReported : m.notReporting}</span></>,
    action: <Link className={link} to="/devices/$deviceId" params={{ deviceId: d.id }}>{m.checkDevice}</Link> });
  for (const p of o.projects) {
    if (p.pending + p.rejected > 0) items.push({ key: `tg-${p.id}`, mark: <PencilMark />,
      body: <><span className="font-medium">{p.name}</span> <span className="text-graphite">{m.notConfirmed(p.rejected, p.pending)}</span></>,
      action: <Link className={link} to="/projects/$projectId" params={{ projectId: p.id }}>{m.seeRollout}</Link> });
    else if (p.targets === 0) items.push({ key: `nt-${p.id}`, mark: <PencilMark />,
      body: <><span className="font-medium">{p.name}</span> <span className="text-graphite">{m.notRunning}</span></>,
      action: <Link className={link} to="/projects/$projectId" params={{ projectId: p.id }}>{m.setUp}</Link> });
  }

  return (
    <Page>
      <PageHeader title={m.title} description={m.description} />
      <Panel title={m.needsYou}>
        {items.length === 0
          ? <p className="px-4 py-6 text-graphite">{m.allClear}</p>
          : <ul>{items.map((it) => (
              <li key={it.key} className={`flex flex-wrap items-center gap-x-3 gap-y-1 ${rowClass}`}>
                {it.mark}
                <p className="min-w-0 flex-1">{it.body}</p>
                {it.action}
              </li>
            ))}</ul>}
      </Panel>
      <div className="grid gap-6 lg:grid-cols-2">
        <Panel title={m.devices} actions={<Link to="/devices" className={`text-sm ${link}`}>{m.allDevices}</Link>}>
          {o.devices.length === 0 && <Empty title={m.noDevices}>{m.noDevicesBody(<code>bounded pair</code>)}</Empty>}
          <ul>{o.devices.map((d) => (
            <li key={d.id} className={`flex items-center justify-between gap-3 ${rowClass}`}>
              <Link to="/devices/$deviceId" params={{ deviceId: d.id }} className="font-medium hover:underline">{d.name}</Link>
              <FreshnessLabel freshness={d.connection} at={d.received_at} />
            </li>
          ))}</ul>
        </Panel>
        <Panel title={m.projects} actions={<Link to="/projects" className={`text-sm ${link}`}>{m.allProjects}</Link>}>
          {o.projects.length === 0 && <Empty title={m.noProjects} action={<Link to="/projects" className={link}>{m.createProject}</Link>} />}
          <ul>{o.projects.map((p) => (
            <li key={p.id} className={`flex items-center justify-between gap-3 ${rowClass}`}>
              <Link to="/projects/$projectId" params={{ projectId: p.id }} className="font-medium hover:underline">{p.name}</Link>
              <span className="text-sm text-graphite">{p.targets ? m.inEffect(p.applied, p.targets) : m.notRolledOut}</span>
            </li>
          ))}</ul>
        </Panel>
      </div>
    </Page>
  );
}

/** Empty account: the three steps are a real sequence, so they are numbered. */
function FirstRun() {
  const t = useT(); const m = t.firstRun;
  const steps: [string, ReactNode, string[]][] = [
    [m.pairTitle, <PairInstructions />, []],
    [m.projectTitle, <>{m.projectBody} <Link to="/projects" className={link}>{m.projectLink}</Link></>, []],
    [m.rolloutTitle, m.rolloutBody(<code>--host codex</code>), ['bounded hook-install --host claude', 'bounded service-install']],
  ];
  return (
    <Page>
      <PageHeader title={m.title} description={m.description} />
      <ol className="grid max-w-3xl gap-px overflow-hidden rounded-[var(--radius-panel)] border border-rule bg-rule">
        {steps.map(([title, body, commands], i) => (
          <li key={title} className="grid grid-cols-[2rem_1fr] gap-3 bg-surface p-4">
            <span aria-hidden className="grid size-6 place-items-center rounded-full border border-dashed border-graphite text-sm text-graphite">{i + 1}</span>
            <div className="min-w-0"><p className="font-medium">{title}</p><div className="mt-1 text-graphite">{body}</div>{commands.map((c) => <Command key={c}>{c}</Command>)}</div>
          </li>
        ))}
      </ol>
    </Page>
  );
}
